'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    fr: {
      title: 'Précision dans chaque détail',
      subtitle: 'Votre partenaire de confiance pour tous vos besoins de nettoyage',
      description: 'Services de nettoyage professionnels combinant la qualité suisse avec un service personnalisé chaleureux. Nous transformons vos espaces pour améliorer votre qualité de vie.',
      ctaPrimary: 'Obtenir un devis gratuit',
      ctaSecondary: 'Nos services',
      trustBadge1: 'Qualité suisse',
      trustBadge2: 'Service personnalisé',
      trustBadge3: 'Équipe certifiée',
      emergencyContact: 'Contact urgence',
      availability: 'Disponibilité 24/7'
    },
    de: {
      title: 'Präzision in jedem Detail',
      subtitle: 'Ihr vertrauenswürdiger Partner für alle Reinigungsbedürfnisse',
      description: 'Professionelle Reinigungsdienste, die Schweizer Qualität mit warmem, persönlichem Service verbinden. Wir verwandeln Ihre Räume, um Ihre Lebensqualität zu verbessern.',
      ctaPrimary: 'Kostenloses Angebot erhalten',
      ctaSecondary: 'Unsere Dienstleistungen',
      trustBadge1: 'Schweizer Qualität',
      trustBadge2: 'Persönlicher Service',
      trustBadge3: 'Zertifiziertes Team',
      emergencyContact: 'Notfallkontakt',
      availability: 'Verfügbarkeit 24/7'
    }
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;

  return (
    <section className={`relative bg-gradient-to-br from-primary via-primary to-secondary overflow-hidden ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-20 lg:py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-accent/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Icon name="SparklesIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm font-medium text-primary-foreground">
                {currentContent.trustBadge1}
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-heading font-bold text-primary-foreground mb-6 leading-tight">
              {currentContent.title}
            </h1>

            <p className="text-xl lg:text-2xl text-primary-foreground/90 mb-4 font-medium">
              {currentContent.subtitle}
            </p>

            <p className="text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto lg:mx-0">
              {currentContent.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Link
                href="/quote-request"
                className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">

                {currentContent.ctaPrimary}
                <Icon name="ArrowRightIcon" size={20} variant="solid" />
              </Link>

              <Link
                href="/services"
                className="inline-flex items-center justify-center gap-2 bg-primary-foreground/10 backdrop-blur-sm hover:bg-primary-foreground/20 text-primary-foreground border-2 border-primary-foreground/30 px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-300">

                {currentContent.ctaSecondary}
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </Link>
            </div>

            {/* Emergency Contact */}
            <div className="mb-8 flex justify-center lg:justify-start">
              <a
                href="tel:+41762126506"
                className="inline-flex items-center gap-3 bg-accent/20 backdrop-blur-sm hover:bg-accent/30 text-primary-foreground border-2 border-accent px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg">
                <Icon name="PhoneIcon" size={24} variant="solid" className="text-accent" />
                <div className="text-left">
                  <div className="text-xs font-medium opacity-90">{currentContent.emergencyContact}</div>
                  <div className="text-lg font-bold">+41 76 212 65 06</div>
                </div>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-primary-foreground/90">
                <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-accent" />
                <span className="text-sm font-medium">{currentContent.trustBadge1}</span>
              </div>
              <div className="flex items-center gap-2 text-primary-foreground/90">
                <Icon name="HeartIcon" size={24} variant="solid" className="text-accent" />
                <span className="text-sm font-medium">{currentContent.trustBadge2}</span>
              </div>
              <div className="flex items-center gap-2 text-primary-foreground/90">
                <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-accent" />
                <span className="text-sm font-medium">{currentContent.trustBadge3}</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="aspect-[4/3] bg-surface">
                <img
                  src="/assets/images/polo_shirt_cleaning_scene_three_people.png"
                  alt="Professional cleaning team member in blue uniform smiling while cleaning modern office space with cleaning supplies"
                  className="w-full h-full object-cover" />

              </div>
              {/* Overlay Badge */}
              <div className="absolute bottom-6 left-6 right-6 bg-card/95 backdrop-blur-sm p-4 rounded-lg shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-success rounded-full flex items-center justify-center">
                    <Icon name="StarIcon" size={24} variant="solid" className="text-success-foreground" />
                  </div>
                  <div>
                    <div className="text-sm font-heading font-bold text-foreground">500+ Clients satisfaits</div>
                    <div className="text-xs text-muted-foreground">Dans la région du Locle</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -top-4 -right-4 bg-card rounded-xl shadow-xl p-4 hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                  <Icon name="ClockIcon" size={24} variant="solid" className="text-accent" />
                </div>
                <div>
                  <div className="text-2xl font-heading font-bold text-foreground">24/7</div>
                  <div className="text-xs text-muted-foreground">{currentContent.availability}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;